import { TimeAxisType } from "@/gql/types";
import moment from "moment";
import {
  CmsAsset,
  CmsGlossaryItem,
  CmsLabelPolicy,
  CmsPremiumFeature,
  CmsPricingPlan,
  CmsRiskScoreBenchMark,
  CmsUseCase,
} from "./cms/cms.models";
import {
  LoggedUser,
  InformationType,
  ContextLabel,
  Env,
  ServiceHelper,
  SERVICE,
  TIME_FRAME,
  Insight,
  LaunchEdition,
  CognniApp,
  CognniAppSettings,
  AppTheme,
  InformationTypeTree,
  FoldersContextLabel,
} from "../models";

export interface TimeFrameOption {
  type: TimeAxisType;
  from: string;
  displayName: string;
  disable: boolean;
}

export interface CommonState {
  raw_settings: Record<string, any>;
  useCases: Record<string, CmsUseCase[]>;
  glossary: Record<string, CmsGlossaryItem>;
  assets: Record<string, CmsAsset>;
  score_benchmark: CmsRiskScoreBenchMark[];
  info_types_policies: CmsLabelPolicy[];
  pricing_plans: CmsPricingPlan[];
  partner: { id: string; name: string; icon: string; dark_icon: string };
  premium_features: Record<string, CmsPremiumFeature>;
  env: Env;
  theme: AppTheme;
  isOrgActive: boolean;
  apps: CognniApp[];
  orgAppFlags: { [key: string]: boolean };
  onPrem: { [key: string]: boolean };
  rollout: { [key: string]: boolean };
  appsSettings: {
    [key: string]: CognniAppSettings;
  };
  anonymousUserId: string;
  user: LoggedUser;
  timeFrame: TIME_FRAME;
  timeFrameOptions: TimeFrameOption[];
  selectedTimeFrame: TimeFrameOption;
  isLoadingBasedOn: boolean;
  basedOn: {
    dateFrom: string | number;
    dateTo: string | number;
    entities: {
      [key: string]: number;
    };
    information: {
      [key: string]: number;
    };
    sources: {
      [key: string]: number;
    };
  };
  insights: Insight[];
  abstract_has_mip_labels: Array<InformationType>;
  PII_labels: Array<InformationType>;
  dynamics_labels: Array<InformationType>;
  virtualLabels: Array<InformationType>;
  contextLabels: Array<ContextLabel>;
  foldersContextLabels: Array<FoldersContextLabel>;
  internal_external_abstract_labels: Array<InformationType>;
  customInfoTypes: { [key: string]: InformationType };
  info_types_desc: Record<string, string>;
  info_types_tree: InformationTypeTree[];
  events: { [key: string]: { items: any[]; people: any[] } };
  currentEventId: string;
}

export const SEED: CommonState = {
  raw_settings: {},
  isOrgActive: true,
  theme: "light",
  partner: {
    id: "cognni",
    name: "Cognni",
    icon: "",
    dark_icon: "",
  },
  useCases: {},
  glossary: {},
  assets: {},
  premium_features: {},
  info_types_policies: [],
  pricing_plans: [],
  score_benchmark: [],
  apps: [],
  abstract_has_mip_labels: [],
  PII_labels: [],
  dynamics_labels: [],
  virtualLabels: [],
  contextLabels: [],
  foldersContextLabels: [],
  internal_external_abstract_labels: [],
  orgAppFlags: {},
  onPrem: {},
  rollout: {},
  appsSettings: {},
  anonymousUserId: "anonymous-user",
  timeFrame: TIME_FRAME.THIS_MONTH,
  timeFrameOptions: [
    {
      type: TimeAxisType.Week,
      from: moment().format("YYYY-MM-DD"),
      displayName: "this week",
      disable: false,
    },
    {
      type: TimeAxisType.Week,
      from: moment().subtract(1, "week").format("YYYY-MM-DD"),
      displayName: "last week",
      disable: false,
    },
  ],
  selectedTimeFrame: {
    type: TimeAxisType.Week,
    from: moment().subtract(1, "week").format("YYYY-MM-DD"),
    displayName: "last week",
    disable: false,
  },
  env: {
    analytics: "",
    mock: false,
    env: "",
    env_num: 1,
    videoUrl: "",
    log: true,
    gql: "",
    type: "Regular",
    onboarding: "",
    server: "",
    hostedLogin: "",
  },
  user: {
    trial: {
      active: false,
      endsAt: "",
      exhausted: false,
    },
    partnerId: "",
    services: [],
    dataSourceApplications: [],
    authorization: "",
    email: "",
    paymentEdition: LaunchEdition,
    name: "",
    orgContactEmail: "",
    orgName: "",
    showUI: true,
    auditLogConsent: false,
  },
  isLoadingBasedOn: false,
  basedOn: {
    dateFrom: Date.now(),
    dateTo: Date.now(),
    entities: {
      FILE: 0,
      EMPLOYEE: 0,
      CONTACT: 0,
      FOLDER: 0,
    },
    information: {
      "d5f24583-7595-6d41-0760-a5d11b8be7cf": 0,
      "fd6a3357-4b1f-df40-d9f5-731e35302fe5": 0,
      "2ef1d9da-0ad7-450e-4b09-efd43581ce78": 0,
      "707babad-231f-82bb-014c-8506d3dc784e": 0,
      "e34ebd2f-cd96-22ac-3059-952a7fe01e54": 0,
      "e2bd11ab-9869-5adc-f11c-7d05c7c6fc2b": 0,
    },
    sources: {
      //  [ServiceHelper.asString(SERVICE.DROPBOX)]: 0,
      [ServiceHelper.asString(SERVICE.ONEDRIVE)]: 0,
      [ServiceHelper.asString(SERVICE.EXCHANGE365)]: 0,
      [ServiceHelper.asString(SERVICE.SHAREPOINT)]: 0,
      [ServiceHelper.asString(SERVICE.TEAMS)]: 0,
    },
  },
  insights: [],
  customInfoTypes: {},
  info_types_desc: {},
  info_types_tree: [],
  events: {},
  currentEventId: "",
};
